import React from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { Row } from './Flex';
import { Interactive } from './InteractiveElement';
import { Menu, MenuRef } from './Menu';
import { Text } from './Text';
import { ArrowKeysTrap } from './KeyboardNavigation';

import { cn } from 'domain/core';
import { PageInfo } from 'domain/models';
import { noop, range } from 'domain/utils';

type IPaginationContext = {
	page: PageInfo;
	intPageChange(page: PageInfo): void;
};

const PaginationContext = React.createContext<IPaginationContext>({
	page: {
		number: 0,
		totalPages: 0,
		size: 10,
		totalElements: 0,
	},
	intPageChange: noop,
});

const usePaginationContext = () => React.useContext(PaginationContext);

const PaginationProvider = React.memo(
	({ children, onPageChange, page }: PaginationProps) => {
		const ctx = React.useMemo(
			() => ({ page, intPageChange: onPageChange }),
			[page, onPageChange]
		);
		return (
			<PaginationContext.Provider value={ctx}>
				{children}
			</PaginationContext.Provider>
		);
	}
);

interface PaginationProps extends React.PropsWithChildren {
	page: PageInfo;
	className?: string;
	onPageChange(page: PageInfo): void;
}
const Pagination = (props: PaginationProps) => {
	return (
		<PaginationProvider page={props.page} onPageChange={props.onPageChange}>
			<Row className={cn('pagination', props.className)}>{props.children}</Row>
		</PaginationProvider>
	);
};

type PageRender = (
	ellipsis: boolean,
	onClick: (page: number) => void,
	page?: number
) => React.ReactNode;

interface PagesProps extends React.PropsWithChildren {
	siblings?: number;
	boundaries?: number;
	className?: string;
	ulClassName?: string;
	pageRender?: PageRender;
}
const Pages = (props: PagesProps) => {
	const { page, intPageChange } = usePaginationContext();
	const { range } = usePaginationRange(
		page.totalPages,
		page.number,
		props.siblings,
		props.boundaries
	);

	const onClick = React.useCallback(
		(value: number) => {
			intPageChange({ ...page, number: value });
		},
		[intPageChange, page]
	);

	const onPrevClick = React.useCallback(() => {
		intPageChange({ ...page, number: Math.max(1, page.number - 1) });
	}, [intPageChange, page]);

	const onNextClick = React.useCallback(() => {
		intPageChange({
			...page,
			number: Math.min(page.totalPages, page.number + 1),
		});
	}, [intPageChange, page]);

	return (
		<nav
			role='navigation'
			aria-label='Pagination'
			className={cn('pagination-pages', props.className)}>
			<ul
				className={cn(
					'flex flex-row items-center justify-end gap-2',
					props.className
				)}>
				<ArrowKeysTrap orientation='horizontal'>
					<Page onClick={onPrevClick} page='previous'>
						<ChevronLeftIcon className='h-6 w-6' />
					</Page>
					{props.children ??
						range.map((value, index) => {
							const ellipsis = Boolean(value === 'dots');
							if (props.pageRender) {
								props.pageRender(
									ellipsis,
									onClick,
									!ellipsis ? (value as number) : undefined
								);
							}
							return ellipsis ? (
								<Ellipsis key={index} />
							) : (
								<Page
									page={value.toString()}
									onClick={() => onClick(value as number)}
									active={value === page.number}
									key={index}>
									{value}
								</Page>
							);
						})}
					<Page onClick={onNextClick} page='next'>
						<ChevronRightIcon className='h-6 w-6' />
					</Page>
				</ArrowKeysTrap>
			</ul>
		</nav>
	);
};

interface PageProps extends React.PropsWithChildren {
	page: string;
	className?: string;
	active?: boolean;
	onClick?: VoidFunction;
}
const Page = (props: PageProps) => {
	return (
		<Interactive onInteract={props.onClick}>
			<li
				role='link'
				title={props.page && `Page ${props.page}`}
				aria-label={`Goto page ${props.page}${
					props.active ? ', Current page' : ''
				}`}
				aria-disabled={!props.page || props.active}
				aria-current={props.active}
				tabIndex={props.page ? 0 : -1}
				onKeyDown={(props as any).onKeyDown}
				className={cn(
					'page',
					'flex h-8 w-8 flex-row items-center justify-center rounded bg-neutral-100 transition-colors',
					props.onClick && !props.active
						? 'cursor-pointer hover:bg-neutral-200'
						: 'cursor-default',
					props.active && 'bg-primary-300 text-neutral-100',
					props.className
				)}>
				{props.children}
			</li>
		</Interactive>
	);
};

interface EllipsisProps {
	className?: string;
}
const Ellipsis = (props: EllipsisProps) => {
	return (
		<Page className={cn('page-ellipsis', props.className)} page=''>
			...
		</Page>
	);
};

interface PageSettingsProps extends React.PropsWithChildren {
	className?: string;
}
const PageSettings = (props: PageSettingsProps) => {
	return (
		<Row className={cn('page-settings', '!justify-start', props.className)}>
			{props.children}
		</Row>
	);
};

interface PageSizeSelectProps {
	className?: string;
	options?: number[];
}
const PageSizeSelect = ({
	options = [10, 25, 50, 100],
}: PageSizeSelectProps) => {
	const { page, intPageChange } = usePaginationContext();
	const menuRef = React.useRef<MenuRef>(null);
	const onSizeChange = React.useCallback(
		(size: number) => {
			intPageChange({ ...page, size });
			menuRef.current?.close();
		},
		[intPageChange, page]
	);

	return (
		<Menu.Root ref={menuRef}>
			<Menu.Trigger isTrigger>
				<Text className={cn('font-bold text-primary-400')}>{page.size}</Text>
			</Menu.Trigger>
			<Menu.ItemsGroup>
				{options.map((opt) => (
					<Menu.Item
						key={opt}
						onClick={() => onSizeChange(opt)}
						className={cn('!px-2 !py-1 text-xs')}>
						{opt}
					</Menu.Item>
				))}
			</Menu.ItemsGroup>
		</Menu.Root>
	);
};

Pagination.displayName = 'Pagination';
Pagination.Pages = Pages;
Pagination.Page = Page;
Pagination.Ellipsis = Ellipsis;

Pagination.PageSettings = PageSettings;
Pagination.PageSizeSelect = PageSizeSelect;

export { Pagination };

export function usePaginationRange(
	total: number,
	page: number,
	siblings = 1,
	boundaries = 1
) {
	const paginationRange = React.useMemo((): (number | 'dots')[] => {
		const _total = Math.max(Math.trunc(total), 0);
		const totalPageNumbers = siblings * 2 + 3 + boundaries * 2;
		if (totalPageNumbers >= _total) {
			return range(1, _total);
		}

		const leftSiblingIndex = Math.max(page - siblings, boundaries);
		const rightSiblingIndex = Math.min(page + siblings, _total - boundaries);

		const shouldShowLeftDots = leftSiblingIndex > boundaries + 2;
		const shouldShowRightDots = rightSiblingIndex < _total - (boundaries + 1);

		if (!shouldShowLeftDots && shouldShowRightDots) {
			const leftItemCount = siblings * 2 + boundaries + 2;
			return [
				...range(1, leftItemCount),
				'dots',
				...range(_total - (boundaries - 1), _total),
			];
		}

		if (shouldShowLeftDots && !shouldShowRightDots) {
			const rightItemCount = boundaries + 1 + 2 * siblings;
			return [
				...range(1, boundaries),
				'dots',
				...range(_total - rightItemCount, _total),
			];
		}

		return [
			...range(1, boundaries),
			'dots',
			...range(leftSiblingIndex, rightSiblingIndex),
			'dots',
			...range(_total - boundaries + 1, _total),
		];
	}, [total, siblings, page, boundaries]);

	return {
		range: paginationRange,
	};
}
